import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCheck, FaPen, FaQuoteLeft, FaTrash, FaXmark } from 'react-icons/fa6';
import { sanitizeHtml } from '../../utils/sanitize';
import { decorateDialogueHtmlForRender } from '../../utils/dialogueTheme';
import { normalizeSheetTheme } from '../../constants/characterThemes';
import { getCharacterProfilePath } from '../../utils/characterPaths';
import { getUserProfilePath } from '../../utils/userPaths';
import styles from './PostCard.module.css';

const TALL_POST_THRESHOLD_PX = 350;

const parseBlocks = (content) => {
  const html = content || '';
  const container = document.createElement('div');
  container.innerHTML = html;

  const elementChildren = Array.from(container.children);
  if (elementChildren.length === 0) {
    return [{ tag: 'p', html }];
  }

  const blocks = elementChildren
    .map((el) => ({
      tag: el.tagName.toLowerCase(),
      html: el.innerHTML,
      text: (el.textContent || '').replace(/\u00a0/g, ' ').trim(),
      hasRichContent: Boolean(el.querySelector('img, video, iframe, table, ul, ol, blockquote, pre, hr, .rich-spacer')),
    }))
    .filter((block) => block.text !== '' || block.hasRichContent)
    .map(({ tag, html: blockHtml }) => ({ tag, html: blockHtml }));

  if (blocks.length === 0) {
    return [];
  }

  return blocks;
};

const PostCard = ({
  post,
  onQuote,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
  isRoleplay = false,
  character = null,
  isInlineEditing = false,
  inlineParagraphIndex = null,
  onInlineSave = null,
  onInlineCancel = null,
}) => {
  // Sanitize HTML content to prevent XSS attacks
  const sanitizedContent = decorateDialogueHtmlForRender(sanitizeHtml(post.content));
  const [editableBlocks, setEditableBlocks] = useState([]);
  const [showFooterActions, setShowFooterActions] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!isInlineEditing) return;
    setEditableBlocks(parseBlocks(post.content));
  }, [isInlineEditing, post.content]);

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return undefined;

    const updateFooterActions = () => {
      setShowFooterActions(element.scrollHeight > TALL_POST_THRESHOLD_PX);
    };

    updateFooterActions();
    const observer = new ResizeObserver(updateFooterActions);
    observer.observe(element);

    return () => observer.disconnect();
  }, [post.content, isInlineEditing, inlineParagraphIndex]);

  const rebuiltContent = useMemo(
    () =>
      editableBlocks
        .map((block) => `<${block.tag}>${block.html}</${block.tag}>`)
        .join(''),
    [editableBlocks]
  );
  const parsedBlocks = useMemo(() => parseBlocks(post.content), [post.content]);
  const characterIdentity = [
    [character?.firstName, character?.lastName].filter(Boolean).join(' ').trim(),
    character?.actualPseudo ? `@${character.actualPseudo}` : '',
  ].filter(Boolean).join(' / ');
  const normalizeAlignment = (value) => (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  const alignmentValue = normalizeAlignment(character?.moralAlignment);
  const nameToneClass = alignmentValue.includes('super-vilain') || alignmentValue.includes('vilain')
    ? styles.characterNameVillain
    : alignmentValue.includes('anti-heros') || alignmentValue.includes('antiheros')
      ? styles.characterNameAntiHero
      : alignmentValue.includes('vigilante')
        ? styles.characterNameVigilante
        : alignmentValue.includes('super-heros') || alignmentValue.includes('heros') || alignmentValue.includes('hero')
          ? styles.characterNameHero
          : styles.characterNameNeutral;
  const avatarToneClass = alignmentValue.includes('super-vilain') || alignmentValue.includes('vilain')
    ? styles.avatarToneVillain
    : alignmentValue.includes('anti-heros') || alignmentValue.includes('antiheros')
      ? styles.avatarToneAntiHero
      : alignmentValue.includes('vigilante')
        ? styles.avatarToneVigilante
        : alignmentValue.includes('super-heros') || alignmentValue.includes('heros') || alignmentValue.includes('hero')
          ? styles.avatarToneHero
          : styles.avatarToneNeutral;

  const handleParagraphDoubleClick = (index) => {
    if (!canEdit || !onEdit) return;
    onEdit(post, index);
  };

  const hasActions = Boolean(
    (isInlineEditing && (onInlineSave || onInlineCancel))
    || onQuote
    || (canEdit && onEdit && !isInlineEditing)
    || (canDelete && onDelete)
  );

  const renderPostActions = (positionClassName) => {
    if (!hasActions) return null;

    return (
      <div
        className={`${styles.postActions} ${positionClassName}`}
        role="group"
        aria-label="Actions sur ce post"
      >
        {isInlineEditing && onInlineSave && (
          <button
            type="button"
            onClick={() => onInlineSave(post, rebuiltContent)}
            className={styles.actionButton}
            title="Enregistrer"
            aria-label="Enregistrer"
          >
            <FaCheck className={styles.actionIcon} aria-hidden />
          </button>
        )}
        {isInlineEditing && onInlineCancel && (
          <button
            type="button"
            onClick={() => onInlineCancel(post)}
            className={styles.actionButton}
            title="Annuler"
            aria-label="Annuler"
          >
            <FaXmark className={styles.actionIcon} aria-hidden />
          </button>
        )}
        {onQuote && (
          <button
            type="button"
            onClick={() => onQuote(post)}
            className={styles.actionButton}
            title="Citer"
            aria-label="Citer"
          >
            <FaQuoteLeft className={styles.actionIcon} aria-hidden />
          </button>
        )}
        {canEdit && onEdit && !isInlineEditing && (
          <button
            type="button"
            onClick={() => onEdit(post)}
            className={styles.actionButton}
            title="Éditer"
            aria-label="Éditer"
          >
            <FaPen className={styles.actionIcon} aria-hidden />
          </button>
        )}
        {canDelete && onDelete && (
          <button
            type="button"
            onClick={() => onDelete(post)}
            className={styles.actionButton}
            title="Supprimer"
            aria-label="Supprimer"
          >
            <FaTrash className={styles.actionIcon} aria-hidden />
          </button>
        )}
      </div>
    );
  };

  const playerIsAnonymous = post.player?.isAnonymous ?? !post.authorId;
  const playerPseudo = post.player?.pseudo ?? null;
  const sidebarAvatar = character?.avatar || post.avatar;
  const sidebarName = character?.name || post.author || 'Anonyme';
  const characterProfilePath = getCharacterProfilePath(character);
  const userProfilePath = !playerIsAnonymous && post.authorId ? getUserProfilePath(post.authorId) : null;

  const sidebarAvatarNode = sidebarAvatar ? (
    <img
      src={sidebarAvatar}
      alt={sidebarName}
      className={`${styles.avatarLarge} ${character ? avatarToneClass : styles.avatarToneNeutral}`}
    />
  ) : (
    <div className={`${styles.avatarPlaceholder} ${character ? avatarToneClass : styles.avatarToneNeutral}`}>
      {sidebarName.charAt(0).toUpperCase()}
    </div>
  );

  const sidebarNameNode = (
    <div className={`${styles.characterName} ${character ? nameToneClass : styles.characterNameNeutral}`}>
      {sidebarName}
    </div>
  );

  // Style RP (Style 2 - Sidebar personnage)
  if (isRoleplay) {
    const characterTheme = normalizeSheetTheme(character?.sheetTheme);
    return (
      <div className={styles.postCardRP} data-character-theme={characterTheme}>
        <div className={styles.characterSidebar}>
          {characterProfilePath ? (
            <Link to={characterProfilePath} className={styles.profileLink} title={`Voir la fiche de ${sidebarName}`}>
              {sidebarAvatarNode}
            </Link>
          ) : (
            sidebarAvatarNode
          )}
          <div className={styles.characterDetails}>
            {characterProfilePath ? (
              <Link to={characterProfilePath} className={styles.profileLink} title={`Voir la fiche de ${sidebarName}`}>
                {sidebarNameNode}
              </Link>
            ) : (
              sidebarNameNode
            )}
            {characterIdentity && (
              <div className={styles.characterIdentity}>{characterIdentity}</div>
            )}
            {character?.alias && (
              <div className={styles.characterAlias}>{character.alias}</div>
            )}
            {character?.moralAlignment && (
              <div className={styles.alignmentBadge}>{character.moralAlignment}</div>
            )}
            {post.isAiAssisted && (
              <div className={styles.aiAssistedBadge} title="Message rédigé avec l'aide de l'IA (Mistral)">
                Rédigé avec l&apos;IA
              </div>
            )}
            {character?.factions && character.factions.length > 0 && (
              <div className={styles.factionsList}>
                {character.factions.map((faction, idx) => (
                  <span key={idx} className={styles.factionTag}>{faction}</span>
                ))}
              </div>
            )}
            <div className={styles.playerInfo}>
              {playerIsAnonymous ? (
                <span className={styles.playerAnonymous}>Joueur : Anonyme</span>
              ) : userProfilePath ? (
                <Link to={userProfilePath} className={styles.profileLink} title={`Voir le profil de ${playerPseudo}`}>
                  Joueur : {playerPseudo || 'Inconnu'}
                </Link>
              ) : (
                <span className={styles.playerName}>Joueur : {playerPseudo || 'Inconnu'}</span>
              )}
            </div>
            <div className={styles.postDate}>{post.date}</div>
          </div>
        </div>
        <div className={styles.postMain}>
          {renderPostActions(styles.postActionsHeader)}
          {isInlineEditing ? (
            <div ref={contentRef} className={styles.inlineEditor}>
              {editableBlocks
                .map((block, index) => ({ block, index }))
                .filter(({ index }) => inlineParagraphIndex === null || inlineParagraphIndex === index)
                .map(({ block, index }) => (
                <div key={`${block.tag}-${index}`} className={styles.inlineEditorBlock}>
                  <div className={styles.inlineEditorLabel}>Paragraphe {index + 1}</div>
                  <div
                    className={styles.inlineEditorContent}
                    contentEditable
                    suppressContentEditableWarning
                    dangerouslySetInnerHTML={{ __html: block.html }}
                    onInput={(event) => {
                      const nextBlocks = [...editableBlocks];
                      nextBlocks[index] = { ...nextBlocks[index], html: event.currentTarget.innerHTML };
                      setEditableBlocks(nextBlocks);
                    }}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div ref={contentRef} className={styles.postContent}>
              {parsedBlocks.map((block, index) =>
                React.createElement(block.tag || 'p', {
                  key: `${block.tag || 'p'}-${index}`,
                  className: styles.postParagraph,
                  onDoubleClick: () => handleParagraphDoubleClick(index),
                  dangerouslySetInnerHTML: { __html: decorateDialogueHtmlForRender(sanitizeHtml(block.html)) },
                })
              )}
            </div>
          )}
          {showFooterActions && renderPostActions(styles.postActionsFooter)}
        </div>
      </div>
    );
  }

  // Style HRP (Style 1 - Classique)
  const hrpUserProfilePath = post.authorId ? getUserProfilePath(post.authorId) : null;

  return (
    <div className={styles.postCardHRP}>
      <div className={styles.postHeader}>
        <div className={styles.postAuthor}>
          {post.avatar && (
            <img
              src={post.avatar}
              alt={post.author}
              className={styles.avatar}
            />
          )}
          <div className={styles.authorInfo}>
            {hrpUserProfilePath ? (
              <Link to={hrpUserProfilePath} className={`${styles.authorName} ${styles.profileLink}`}>
                {post.author}
              </Link>
            ) : (
              <span className={styles.authorName}>{post.author}</span>
            )}
            <span className={styles.postDate}>{post.date}</span>
          </div>
        </div>
        {renderPostActions(styles.postActionsHeader)}
      </div>
      <div
        ref={contentRef}
        className={styles.postContent}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      />
      {showFooterActions && renderPostActions(styles.postActionsFooter)}
    </div>
  );
};

export default PostCard;
